import { Fade, Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow, Tooltip } from "@mui/material";
import React, { useState, useCallback, useContext } from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle, Card } from "randmarcomps";
import { Info } from "lucide-react";
import { useGetV4WarehousesQuery } from "randmar-api-client";
import ManufacturerContext from "../../contexts/ManufacturerContext";
import Path from "../../routes/Path";
import { PurchaseOrder } from "../../stores/document/Document";
import CancelPurchaseOrder from "../document/actions/CancelPurchaseOrder";
import CurrencyUtils from "../util/CurrencyUtils";
import FormatDateFromInt from "../util/DateTimeUtil";

interface IProps {
  purchaseOrders?: PurchaseOrder[];
  defaultRowsPerPage?: number;
}

const PurchaseOrdersGrid: React.FC<IProps> = ({ purchaseOrders, defaultRowsPerPage }) => {
  const { applicationId } = useContext(ManufacturerContext);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage || 20);

  const handleChangePage = useCallback((_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const { data: warehouses } = useGetV4WarehousesQuery();

  const getWarehouseNameFromCode = useCallback((code: string | undefined): string => {
    if (!code || !warehouses) {
      return "B/O";
    }
    const warehouse = warehouses.find(w => w.WarehouseId === code);
    return warehouse?.Name || "B/O";
  }, [warehouses]);

  if (!purchaseOrders || purchaseOrders.length === 0)
    return (
      <Alert className="mb-4">
        <Info className="h-4 w-4" />
        <AlertTitle>What are Purchase Orders?</AlertTitle>
        <AlertDescription>
          Purchase Orders are orders placed by Randmar in order to put inventory in our warehouses. Your dedicaded
          department will receive them in the form of orders. To create Purchase Orders, visit the Draft Purchases tab.
        </AlertDescription>
      </Alert>
    );

  return (
    <Card className="w-full">
      <div className="w-full overflow-auto rounded-[20px]">
        <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: "#f9f5ff" }}>
            <TableCell align="center" sx={{ borderColor: "#582c83" }}>PO Number</TableCell>
            <TableCell align="center" sx={{ borderColor: "#582c83" }}>Location</TableCell>
            <TableCell align="center" sx={{ borderColor: "#582c83" }}>PO Date</TableCell>
            <TableCell align="center" sx={{ borderColor: "#582c83" }}>Expected Arrival Date</TableCell>
            <TableCell align="center" sx={{ borderColor: "#582c83" }}>Sub Total</TableCell>
            <TableCell align="center" sx={{ borderColor: "#582c83" }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {purchaseOrders
            .slice()
            .sort((a, b) => (b.purchaseOrderNumber || "").localeCompare(a.purchaseOrderNumber || ""))
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((row, index) => (
              <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }} key={"PurchaseOrder" + index}>
                <Tooltip
                  arrow
                  title={
                    <>
                      <div>Comments: {row.comments}</div>
                      <div>Terms: {row.termsCode}</div>
                    </>
                  }
                  TransitionComponent={Fade}
                >
                  <TableCell align="center"><Link to={Path.document(applicationId, row.purchaseOrderNumber)}> {row.purchaseOrderNumber}</Link></TableCell>
                </Tooltip>
                <TableCell align="center">{getWarehouseNameFromCode(row.location)}</TableCell>
                <TableCell align="center">{FormatDateFromInt(row.purchaseOrderDate)}</TableCell>
                <TableCell align="center">{FormatDateFromInt(row.expectedArrivalDate)}</TableCell>
                <TableCell align="center"><FormattedNumber {...CurrencyUtils.format} value={row.subTotal || 0} /></TableCell>
                <TableCell><CancelPurchaseOrder purchaseOrderNumber={row.purchaseOrderNumber || ""} fab withReload /></TableCell>
              </TableRow>
            ))}
        </TableBody>
        {purchaseOrders.length > rowsPerPage &&
          <TableFooter>
            <TableRow>
              <TablePagination
                count={purchaseOrders.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableRow>
          </TableFooter>}
      </Table>
      </div>
    </Card>
  );
};

export default PurchaseOrdersGrid;
